import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import type { MentionableEntity } from './RichTextField';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { RichTextField } from './RichTextField';
import { AddItemButton } from './AddItemButton';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { PanelPageHeader } from './PanelPageHeader';
import { HeaderCreateActions } from './HeaderPageActions';
import { StorylinesEmptyState } from './StorylinesEmptyState';
import { StorylinePanel } from './StorylinePanel';
import { ConfirmDialog } from './ConfirmDialog';
import { SceneFormDialog } from './SceneFormDialog';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import type { Scene } from './Scenes';
import {
  createSceneLink,
  getLinkableScenes,
  isSceneLinkedToContext,
  removeSceneLinks,
  removeSceneOutboundLink,
  type SceneLinkContext,
} from '../utils/scene-links';
import { getRelatedEntitiesFor } from '../utils/related-entities';
import { removeMentionFromHtml, type RelatedEntity } from '../utils/entity-utils';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';

export interface Storyline {
  id: string;
  title: string;
  description?: string;
}

function normalizeStorylineDescription(html: string): string | undefined {
  const trimmed = html.trim();
  if (!trimmed) return undefined;
  if (trimmed === '<p></p>' || trimmed === '<p><br></p>' || trimmed === '<p><br/></p>') return undefined;
  return trimmed;
}

interface StorylinesProps {
  documentId?: string;
  projectId?: string;
  isSidebar?: boolean;
  onClose?: () => void;
  initialStorylineId?: string;
}

interface EntityReference {
  sourceType: string;
  sourceId: string;
  sourceDocumentId: string | null;
  targetType: string;
  targetId: string;
  targetDocumentId: string | null;
}

export const Storylines: React.FC<StorylinesProps> = ({
  documentId: propDocumentId,
  projectId: propProjectId,
  isSidebar = false,
  onClose,
  initialStorylineId,
}) => {
  const params = useParams<{ projectId?: string; documentId: string }>();
  const documentId = propDocumentId || params.documentId;
  const projectId = propProjectId || params.projectId;
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => new URLSearchParams(window.location.search).get('masterKey'));

  const [storylines, setStorylines] = useState<Storyline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStorylineTitle, setNewStorylineTitle] = useState('');
  const [newStorylineDescription, setNewStorylineDescription] = useState('');
  const [addingStoryline, setAddingStoryline] = useState(false);
  const [editingStorylineId, setEditingStorylineId] = useState<string | null>(null);
  const [deletingStorylineId, setDeletingStorylineId] = useState<string | null>(null);
  const [deleteConfirmStoryline, setDeleteConfirmStoryline] = useState<Storyline | null>(null);

  const [scenes, setScenes] = useState<Scene[]>([]);
  const [entityRefs, setEntityRefs] = useState<EntityReference[]>([]);
  const [showSceneModal, setShowSceneModal] = useState(false);
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [newSceneTitle, setNewSceneTitle] = useState('');
  const [newSceneDescription, setNewSceneDescription] = useState('');
  const [sceneLinkContext, setSceneLinkContext] = useState<SceneLinkContext | null>(null);
  const [savingScene, setSavingScene] = useState(false);
  const [unlinkingScene, setUnlinkingScene] = useState(false);
  const [deletingSceneId, setDeletingSceneId] = useState<string | null>(null);
  const [deleteConfirmScene, setDeleteConfirmScene] = useState<Scene | null>(null);

  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);

  useEffect(() => {
    const fetchMentionableEntities = async (): Promise<void> => {
      if (!projectId || (!token && !masterKey)) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    fetchMentionableEntities();
  }, [projectId, token, masterKey]);

  const fetchStorylines = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !documentId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/storylines?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/storylines`;
      const response = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch storylines');
      const data = await response.json();
      setStorylines(Array.isArray(data.storylines) ? data.storylines : []);
    } catch (err) {
      console.error('Error fetching storylines:', err);
      setError(t('failed_to_load_storylines'));
    } finally {
      setLoading(false);
    }
  }, [token, documentId, masterKey, t]);

  const fetchScenes = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !documentId) return;
    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/scenes`;
      const response = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch scenes');
      const data = await response.json();
      setScenes(Array.isArray(data.scenes) ? data.scenes : []);
    } catch (err) {
      console.error('Error fetching scenes:', err);
    }
  }, [token, documentId, masterKey]);

  const fetchEntityRefs = useCallback(async (): Promise<void> => {
    if (!projectId || (!token && !masterKey)) return;
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        setEntityRefs(Array.isArray(data.references) ? data.references : []);
      } else {
        setEntityRefs([]);
      }
    } catch {
      setEntityRefs([]);
    }
  }, [projectId, token, masterKey]);

  useEffect(() => {
    fetchStorylines();
  }, [fetchStorylines]);

  useEffect(() => {
    fetchScenes();
  }, [fetchScenes]);

  useEffect(() => {
    fetchEntityRefs();
  }, [fetchEntityRefs]);

  useEffect(() => {
    if (!initialStorylineId || loading) return;
    const storyline = storylines.find((s) => s.id === initialStorylineId);
    if (storyline) {
      setEditingStorylineId(initialStorylineId);
      setNewStorylineTitle(storyline.title);
      setNewStorylineDescription(storyline.description ?? '');
      setShowAddModal(true);
    }
  }, [initialStorylineId, loading, storylines]);

  const scenesByStoryline = useMemo(() => {
    const map = new Map<string, Scene[]>();
    if (!documentId) return map;
    for (const storyline of storylines) {
      const linkedSceneIds = new Set(
        entityRefs
          .filter(
            (ref) =>
              ref.sourceType === 'scene' &&
              ref.sourceDocumentId === documentId &&
              ref.targetType === 'storyline' &&
              ref.targetId === storyline.id
          )
          .map((ref) => ref.sourceId)
      );
      const linkedScenes = scenes
        .filter((scene) => linkedSceneIds.has(scene.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      map.set(storyline.id, linkedScenes);
    }
    return map;
  }, [storylines, scenes, entityRefs, documentId]);

  const linkableScenes = useMemo(() => {
    if (!documentId || !sceneLinkContext) return [];
    return getLinkableScenes(scenes, entityRefs, documentId, sceneLinkContext);
  }, [documentId, scenes, entityRefs, sceneLinkContext]);

  const sceneIsLinkedToContext = Boolean(
    editingSceneId &&
      documentId &&
      sceneLinkContext &&
      isSceneLinkedToContext(entityRefs, editingSceneId, documentId, sceneLinkContext),
  );

  const sceneRelatedEntities = useMemo(() => {
    if (!editingSceneId || !documentId) return { linksTo: [], referencedIn: [] };
    return getRelatedEntitiesFor(
      'scene',
      editingSceneId,
      documentId,
      entityRefs,
      mentionableEntities,
    );
  }, [editingSceneId, documentId, entityRefs, mentionableEntities]);

  const handleRemoveSceneLinksTo = useCallback(
    async (entity: RelatedEntity): Promise<void> => {
      if (!editingSceneId || !documentId || !projectId || !token) return;
      try {
        const removed = await removeSceneOutboundLink({
          projectId,
          token,
          sceneId: editingSceneId,
          documentId,
          entity,
        });
        if (!removed) {
          setError(t('Failed to remove entity reference'));
          return;
        }
        const newDesc = removeMentionFromHtml(newSceneDescription || '', entity);
        if (newDesc !== (newSceneDescription || '')) {
          const order = scenes.find((s) => s.id === editingSceneId)?.order ?? 1;
          const url = masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}`;
          const res = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              order,
              title: newSceneTitle.trim() || scenes.find((s) => s.id === editingSceneId)?.title,
              description: newDesc.trim() || undefined,
            }),
          });
          if (!res.ok) throw new Error('Failed to update');
          setNewSceneDescription(newDesc.trim() || '');
          await fetchScenes();
        }
        await fetchEntityRefs();
      } catch {
        setError(t('Failed to remove entity reference'));
      }
    },
    [
      editingSceneId,
      documentId,
      projectId,
      token,
      newSceneDescription,
      newSceneTitle,
      scenes,
      masterKey,
      fetchScenes,
      fetchEntityRefs,
      t,
    ],
  );

  const handleRemoveSceneReferencedIn = useCallback(
    async (entity: RelatedEntity): Promise<void> => {
      if (!token || !projectId || !editingSceneId || !documentId) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'scene',
            targetId: editingSceneId,
            targetDocumentId: documentId,
          }),
        });
        if (res.ok) {
          await fetchEntityRefs();
        } else {
          setError(t('Failed to remove entity reference'));
        }
      } catch {
        setError(t('Failed to remove entity reference'));
      }
    },
    [editingSceneId, documentId, projectId, token, fetchEntityRefs, t],
  );

  const closeModal = (): void => {
    setShowAddModal(false);
    setEditingStorylineId(null);
    setNewStorylineTitle('');
    setNewStorylineDescription('');
  };

  const handleSave = async (): Promise<void> => {
    if (!newStorylineTitle.trim() || addingStoryline || !documentId) return;
    const isEditing = editingStorylineId !== null;
    try {
      setAddingStoryline(true);
      const url = isEditing
        ? (masterKey
            ? `${API_BASE}/api/documents/${documentId}/storylines/${editingStorylineId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/storylines/${editingStorylineId}`)
        : (masterKey
            ? `${API_BASE}/api/documents/${documentId}/storylines?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/storylines`);
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: newStorylineTitle.trim(),
          description: (newStorylineDescription || '').trim() || undefined,
        }),
      });
      if (!response.ok) throw new Error(isEditing ? 'Failed to update storyline' : 'Failed to create storyline');
      await fetchStorylines();
      closeModal();
    } catch (err) {
      console.error(isEditing ? 'Error updating storyline:' : 'Error creating storyline:', err);
      setError(isEditing ? t('failed_to_update_storyline') : t('failed_to_create_storyline'));
    } finally {
      setAddingStoryline(false);
    }
  };

  const handleStorylineUpdate = useCallback(async (
    storylineId: string,
    updates: { title?: string; description?: string }
  ): Promise<boolean> => {
    if ((!token && !masterKey) || !documentId) return false;
    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/storylines/${storylineId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/storylines/${storylineId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update storyline');
      const data: { storyline?: Storyline } = await response.json();
      if (data.storyline) {
        setStorylines((prev) =>
          prev.map((storyline) =>
            storyline.id === storylineId ? { ...storyline, ...data.storyline } : storyline
          )
        );
      }
      if (updates.description !== undefined) {
        await fetchEntityRefs();
      }
      return true;
    } catch (err) {
      console.error('Error updating storyline:', err);
      setError(t('failed_to_update_storyline'));
      return false;
    }
  }, [token, masterKey, documentId, fetchEntityRefs, t]);

  const handleDelete = async (storyline: Storyline): Promise<void> => {
    if (!storyline.id || deletingStorylineId === storyline.id || !documentId) return;
    try {
      setDeletingStorylineId(storyline.id);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/storylines/${storyline.id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/storylines/${storyline.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to delete storyline');
      await fetchStorylines();
    } catch (err) {
      console.error('Error deleting storyline:', err);
      setError(t('failed_to_delete_storyline'));
    } finally {
      setDeletingStorylineId(null);
    }
  };

  const closeSceneModal = (): void => {
    setShowSceneModal(false);
    setEditingSceneId(null);
    setNewSceneTitle('');
    setNewSceneDescription('');
    setSceneLinkContext(null);
  };

  const openAddSceneModal = (storyline: Storyline): void => {
    setEditingSceneId(null);
    setNewSceneTitle('');
    setNewSceneDescription('');
    setSceneLinkContext({ type: 'storyline', id: storyline.id });
    setShowSceneModal(true);
  };

  const openEditSceneModal = (scene: Scene, storyline: Storyline): void => {
    setEditingSceneId(scene.id);
    setNewSceneTitle(scene.title);
    setNewSceneDescription(scene.description ?? '');
    setSceneLinkContext({ type: 'storyline', id: storyline.id });
    setShowSceneModal(true);
  };

  const handleSelectExistingScene = (scene: Scene): void => {
    setEditingSceneId(scene.id);
    setNewSceneTitle(scene.title);
    setNewSceneDescription(scene.description ?? '');
  };

  const handleSaveScene = async (): Promise<void> => {
    if (!newSceneTitle.trim() || savingScene || !documentId) return;
    const isEditing = editingSceneId !== null;
    const order = isEditing
      ? scenes.find((s) => s.id === editingSceneId)?.order ?? 1
      : Math.max(0, ...scenes.map((s) => s.order ?? 0)) + 1;
    try {
      setSavingScene(true);
      const url = isEditing
        ? (masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}`)
        : (masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes`);
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          order,
          title: newSceneTitle.trim(),
          description: (newSceneDescription || '').trim() || undefined,
        }),
      });
      if (!response.ok) throw new Error(isEditing ? 'Failed to update scene' : 'Failed to create scene');

      const data: { scene?: Scene } = await response.json().catch(() => ({}));
      const sceneId = isEditing ? editingSceneId : data.scene?.id;
      if (
        sceneId &&
        sceneLinkContext &&
        projectId &&
        token &&
        !isSceneLinkedToContext(entityRefs, sceneId, documentId, sceneLinkContext)
      ) {
        const linked = await createSceneLink({
          projectId,
          token,
          sceneId,
          documentId,
          context: sceneLinkContext,
        });
        if (!linked) {
          setError(t('failed_to_link_scene'));
        }
      }

      await fetchScenes();
      await fetchEntityRefs();
      closeSceneModal();
    } catch (err) {
      console.error(isEditing ? 'Error updating scene:' : 'Error creating scene:', err);
      setError(isEditing ? t('failed_to_update_scene') : t('failed_to_create_scene'));
    } finally {
      setSavingScene(false);
    }
  };

  const handleUnlinkScene = async (): Promise<void> => {
    if (!editingSceneId || !sceneLinkContext || !documentId || !projectId || !token || unlinkingScene) {
      return;
    }
    try {
      setUnlinkingScene(true);
      const unlinked = await removeSceneLinks({
        projectId,
        token,
        sceneId: editingSceneId,
        documentId,
        context: sceneLinkContext,
      });
      if (!unlinked) {
        setError(t('failed_to_unlink_scene'));
        return;
      }
      closeSceneModal();
      await fetchScenes();
      await fetchEntityRefs();
    } catch (err) {
      console.error('Error unlinking scene:', err);
      setError(t('failed_to_unlink_scene'));
    } finally {
      setUnlinkingScene(false);
    }
  };

  const handleDeleteScene = async (scene: Scene): Promise<void> => {
    if (!scene.id || deletingSceneId === scene.id || !documentId) return;
    try {
      setDeletingSceneId(scene.id);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to delete scene');
      if (editingSceneId === scene.id) {
        closeSceneModal();
      }
      await fetchScenes();
      await fetchEntityRefs();
    } catch (err) {
      console.error('Error deleting scene:', err);
      setError(t('failed_to_delete_scene'));
    } finally {
      setDeletingSceneId(null);
    }
  };

  if (loading) {
    if (isSidebar) {
      return (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          {t('loading')}
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>
        {t('loading')}
      </div>
    );
  }

  if (error) {
    if (isSidebar) {
      return (
        <div className="flex flex-col gap-2 p-4">
          <div className="text-sm text-red-600">{error}</div>
          <Button size="sm" variant="outline" onClick={() => fetchStorylines()} className="w-full">
            {t('retry')}
          </Button>
        </div>
      );
    }
    return (
      <div>
        <PanelPageHeader
          onClose={onClose}
          closeAriaLabel={t('close')}
        />
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '3rem' }}>{error}</div>
      </div>
    );
  }

  const addStorylineButton = (
    <AddItemButton onClick={() => setShowAddModal(true)} label={t('add_storyline')} />
  );

  const content = (
    <div className={PANEL_BODY_FILL_CLASSNAME}>
      {!isSidebar ? (
        <HeaderCreateActions hasItems={storylines.length > 0}>
          {addStorylineButton}
        </HeaderCreateActions>
      ) : null}
      <PanelPageHeader
        onClose={!isSidebar ? onClose : undefined}
        closeAriaLabel={t('close')}
        endActions={isSidebar && storylines.length > 0 ? addStorylineButton : undefined}
      />

      {storylines.length === 0 ? (
        <StorylinesEmptyState canEdit onAdd={() => setShowAddModal(true)} />
      ) : (
        <div className="flex flex-col gap-4">
          {storylines.map((storyline) => (
            <StorylinePanel
              key={storyline.id}
              storyline={storyline}
              scenes={scenesByStoryline.get(storyline.id) ?? []}
              mentionableEntities={mentionableEntities}
              projectId={projectId}
              documentId={documentId}
              onEditStoryline={() => {
                setEditingStorylineId(storyline.id);
                setNewStorylineTitle(storyline.title);
                setNewStorylineDescription(storyline.description ?? '');
                setShowAddModal(true);
              }}
              onDeleteStoryline={() => setDeleteConfirmStoryline(storyline)}
              onTitleChange={(title) => handleStorylineUpdate(storyline.id, { title })}
              onDescriptionChange={(description) => {
                void handleStorylineUpdate(storyline.id, {
                  description: normalizeStorylineDescription(description) ?? '',
                });
              }}
              onAddScene={() => openAddSceneModal(storyline)}
              onEditScene={(scene) => openEditSceneModal(scene, storyline)}
              onDeleteScene={(scene) => setDeleteConfirmScene(scene)}
              deletingSceneId={deletingSceneId}
            />
          ))}
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
            {t('add_storyline')}
          </button>
        </div>
      )}
    </div>
  );

  const addModal = (
    <Dialog open={showAddModal} onOpenChange={(open) => !open && !addingStoryline && closeModal()}>
      <DialogContent className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}>
        <DialogTitle className="sr-only">
          {editingStorylineId ? t('edit_storyline') : t('add_storyline')}
        </DialogTitle>
        <EntityDetailModalLayout
          header={
            <EntityDetailModalTitle>
              {editingStorylineId ? t('edit_storyline') : t('add_storyline')}
            </EntityDetailModalTitle>
          }
          footer={
            <DialogFooter className="gap-2 sm:justify-end">
              <CancelButton onClick={closeModal} />
              <Button
                onClick={handleSave}
                dialogPrimary
                className="inline-flex items-center gap-1.5"
                disabled={!newStorylineTitle.trim() || addingStoryline}
              >
                {addingStoryline ? (
                  editingStorylineId ? t('saving') : t('adding')
                ) : (
                  <>
                    {editingStorylineId ? t('save') : t('add')}
                    <DialogPrimaryShortcut className="ml-0.5" />
                  </>
                )}
              </Button>
            </DialogFooter>
          }
        >
          <div className="space-y-6 p-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t('title')} *
              </label>
              <input
                type="text"
                value={newStorylineTitle}
                onChange={(e) => setNewStorylineTitle(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-3 text-base text-foreground"
                placeholder={t('storyline_title_placeholder')}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t('description')}
              </label>
              <RichTextField
                value={newStorylineDescription}
                onChange={setNewStorylineDescription}
                placeholder={t('storyline_description_placeholder')}
                minHeight="150px"
                mentionableEntities={mentionableEntities}
              />
            </div>
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );

  const sceneModal = (
    <SceneFormDialog
      open={showSceneModal}
      isEditing={editingSceneId !== null}
      isSaving={savingScene}
      title={newSceneTitle}
      description={newSceneDescription}
      mentionableEntities={mentionableEntities}
      linkableScenes={linkableScenes}
      projectId={projectId}
      relatedLinksTo={sceneRelatedEntities.linksTo}
      relatedReferencedIn={sceneRelatedEntities.referencedIn}
      deleteDisabled={deletingSceneId === editingSceneId}
      unlinkDisabled={unlinkingScene}
      showUnlink={sceneIsLinkedToContext}
      onTitleChange={setNewSceneTitle}
      onDescriptionChange={setNewSceneDescription}
      onSelectExistingScene={handleSelectExistingScene}
      onSave={() => {
        void handleSaveScene();
      }}
      onCancel={closeSceneModal}
      onDelete={
        editingSceneId
          ? () => {
              const scene = scenes.find((s) => s.id === editingSceneId);
              if (scene) setDeleteConfirmScene(scene);
            }
          : undefined
      }
      onUnlink={() => {
        void handleUnlinkScene();
      }}
      onRemoveLinksTo={(entity) => {
        void handleRemoveSceneLinksTo(entity);
      }}
      onRemoveReferencedIn={(entity) => {
        void handleRemoveSceneReferencedIn(entity);
      }}
    />
  );

  const deleteStorylineDialog = (
    <ConfirmDialog
      open={deleteConfirmStoryline !== null}
      onOpenChange={(open) => !open && setDeleteConfirmStoryline(null)}
      title={t('delete_storyline')}
      description={t('confirm_delete_storyline')}
      confirmLabel={t('delete_storyline')}
      onConfirm={() => {
        if (deleteConfirmStoryline) void handleDelete(deleteConfirmStoryline);
      }}
    />
  );

  const deleteSceneDialog = (
    <ConfirmDialog
      open={deleteConfirmScene !== null}
      onOpenChange={(open) => !open && setDeleteConfirmScene(null)}
      title={t('delete_scene')}
      description={t('confirm_delete_scene')}
      confirmLabel={t('delete_scene')}
      onConfirm={() => {
        if (deleteConfirmScene) void handleDeleteScene(deleteConfirmScene);
      }}
    />
  );

  if (isSidebar) {
    return (
      <>
        <div className={`${PANEL_BODY_FILL_CLASSNAME} gap-4 overflow-auto`} style={{ padding: '1rem' }}>
          {content}
        </div>
        {addModal}
        {sceneModal}
        {deleteStorylineDialog}
        {deleteSceneDialog}
      </>
    );
  }

  return (
    <>
      {content}
      {addModal}
      {sceneModal}
      {deleteStorylineDialog}
      {deleteSceneDialog}
    </>
  );
};
